import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Textarea } from './ui/textarea';
import api from '../utils/api';
import { toast } from 'sonner';
import { 
  Plus, 
  Save, 
  Trash2,
  Edit,
  Server,
  Network,
  Database,
  Zap,
  HardDrive,
  Shield
} from 'lucide-react';

const equipmentTypes = [
  { value: 'server', label: 'Server', icon: Server, color: '#3b82f6' },
  { value: 'switch', label: 'Switch', icon: Network, color: '#10b981' },
  { value: 'router', label: 'Router', icon: Network, color: '#f59e0b' },
  { value: 'firewall', label: 'Firewall', icon: Shield, color: '#ef4444' },
  { value: 'storage', label: 'Storage', icon: Database, color: '#8b5cf6' },
  { value: 'ups', label: 'UPS', icon: Zap, color: '#eab308' },
  { value: 'pdu', label: 'PDU', icon: Zap, color: '#06b6d4' },
  { value: 'patch_panel', label: 'Patch Panel', icon: Network, color: '#6366f1' },
  { value: 'other', label: 'Other', icon: HardDrive, color: '#64748b' }
];

const RackLayouts = ({ clientId }) => {
  const [racks, setRacks] = useState([]);
  const [selectedRack, setSelectedRack] = useState(null);
  const [showNewRackDialog, setShowNewRackDialog] = useState(false);
  const [showEditRackDialog, setShowEditRackDialog] = useState(false);
  const [showEquipmentDialog, setShowEquipmentDialog] = useState(false);
  const [editingEquipment, setEditingEquipment] = useState(null);
  const [draggedEquipment, setDraggedEquipment] = useState(null);

  const [newRack, setNewRack] = useState({
    name: '',
    location: '',
    rack_height: 42,
    total_power_capacity: null,
    notes: ''
  });

  const [newEquipment, setNewEquipment] = useState({
    name: '',
    equipment_type: 'server',
    manufacturer: '',
    model: '',
    u_position: 1,
    u_height: 1,
    width: 'full',
    power_watts: null,
    serial_number: '',
    notes: ''
  });

  useEffect(() => {
    if (clientId) {
      fetchRacks();
    }
  }, [clientId]);

  const fetchRacks = async () => {
    try {
      const response = await api.get(`/racks?client_id=${clientId}`);
      setRacks(response.data);
      if (response.data.length > 0 && !selectedRack) {
        setSelectedRack(response.data[0]);
      }
    } catch (error) {
      toast.error('Failed to load racks');
    }
  };

  const resetRackForm = () => {
    setNewRack({
      name: '',
      location: '',
      rack_height: 42,
      total_power_capacity: null,
      notes: ''
    });
  };

  const resetEquipmentForm = () => {
    setNewEquipment({
      name: '',
      equipment_type: 'server',
      manufacturer: '',
      model: '',
      u_position: 1,
      u_height: 1,
      width: 'full',
      power_watts: null,
      serial_number: '',
      notes: ''
    });
    setEditingEquipment(null);
  };

  const handleCreateRack = async (e) => {
    e.preventDefault();
    try {
      await api.post('/racks', {
        ...newRack,
        client_id: clientId
      });
      toast.success('Rack created successfully!');
      setShowNewRackDialog(false);
      resetRackForm();
      fetchRacks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create rack');
    }
  };

  const handleUpdateRack = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/racks/${selectedRack.id}`, newRack);
      toast.success('Rack updated successfully!');
      setShowEditRackDialog(false);
      resetRackForm();
      fetchRacks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update rack');
    }
  };

  const handleDeleteRack = async (rackId) => {
    if (!window.confirm('Are you sure you want to delete this rack?')) return;
    
    try {
      await api.delete(`/racks/${rackId}`);
      toast.success('Rack deleted successfully!');
      setSelectedRack(null);
      fetchRacks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete rack');
    }
  };

  const handleAddEquipment = async (e) => {
    e.preventDefault();
    try {
      if (editingEquipment) {
        await api.put(`/racks/${selectedRack.id}/equipment/${editingEquipment.id}`, newEquipment);
        toast.success('Equipment updated successfully!');
      } else {
        await api.post(`/racks/${selectedRack.id}/equipment`, newEquipment);
        toast.success('Equipment added successfully!');
      }
      setShowEquipmentDialog(false);
      resetEquipmentForm();
      fetchRacks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save equipment');
    }
  };

  const handleDeleteEquipment = async (equipmentId) => {
    if (!window.confirm('Are you sure you want to delete this equipment?')) return;
    
    try {
      await api.delete(`/racks/${selectedRack.id}/equipment/${equipmentId}`);
      toast.success('Equipment deleted successfully!');
      fetchRacks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete equipment');
    }
  };

  const openEditRackDialog = () => {
    setNewRack({
      name: selectedRack.name,
      location: selectedRack.location || '',
      rack_height: selectedRack.rack_height,
      total_power_capacity: selectedRack.total_power_capacity,
      notes: selectedRack.notes || ''
    });
    setShowEditRackDialog(true);
  };

  const openEditEquipmentDialog = (equipment) => {
    setEditingEquipment(equipment);
    setNewEquipment({
      name: equipment.name,
      equipment_type: equipment.equipment_type,
      manufacturer: equipment.manufacturer || '',
      model: equipment.model || '',
      u_position: equipment.u_position,
      u_height: equipment.u_height,
      width: equipment.width || 'full',
      power_watts: equipment.power_watts,
      serial_number: equipment.serial_number || '',
      notes: equipment.notes || ''
    });
    setShowEquipmentDialog(true);
  };

  const calculateTotalPower = () => {
    if (!selectedRack?.equipment) return 0;
    return selectedRack.equipment.reduce((sum, eq) => sum + (eq.power_watts || 0), 0);
  };

  const getOccupiedUPositions = () => {
    if (!selectedRack?.equipment) return { full: new Set(), left: new Set(), right: new Set() };
    const occupied = { full: new Set(), left: new Set(), right: new Set() };
    
    selectedRack.equipment.forEach(eq => {
      const width = eq.width || 'full';
      for (let i = eq.u_position; i < eq.u_position + eq.u_height; i++) {
        if (width === 'full') {
          occupied.full.add(i);
        } else if (width === 'half-left') {
          occupied.left.add(i);
        } else if (width === 'half-right') {
          occupied.right.add(i);
        }
      }
    });
    return occupied;
  };

  const renderRackView = () => {
    if (!selectedRack) return null;

    const occupied = getOccupiedUPositions();
    const rackUnits = [];
    const renderedUs = new Set(); // Track which U positions we've already rendered

    for (let u = selectedRack.rack_height; u >= 1; u--) {
      // Skip if this U was already rendered as part of multi-U equipment
      if (renderedUs.has(u)) continue;

      // Find all equipment at this U position (starting position)
      const equipmentAtU = selectedRack.equipment.filter(eq => eq.u_position === u);
      const leftEq = equipmentAtU.find(eq => (eq.width || 'full') === 'half-left');
      const rightEq = equipmentAtU.find(eq => (eq.width || 'full') === 'half-right');
      const fullEq = equipmentAtU.find(eq => (eq.width || 'full') === 'full');

      const renderEquipmentBlock = (equipment, width) => {
        const equipmentConfig = equipmentTypes.find(t => t.value === equipment.equipment_type);
        const Icon = equipmentConfig?.icon || Server;
        const isHalfWidth = width.startsWith('half-');

        return (
          <div
            className={`relative border border-slate-300 bg-white hover:shadow-md transition-shadow cursor-pointer group ${isHalfWidth ? 'w-1/2' : 'w-full'}`}
            style={{
              height: `${equipment.u_height * 40}px`,
              backgroundColor: equipmentConfig?.color + '15'
            }}
            onClick={() => openEditEquipmentDialog(equipment)}
          >
            <div className="absolute inset-0 flex items-center px-2">
              <div className="flex items-center gap-1 flex-1 min-w-0">
                <Icon className="w-4 h-4 flex-shrink-0" style={{ color: equipmentConfig?.color }} />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-xs truncate">{equipment.name}</div>
                  <div className="text-xs text-slate-600 truncate">
                    {equipment.manufacturer || equipment.model ? `${equipment.manufacturer || ''} ${equipment.model || ''}`.trim() : ''}
                  </div>
                </div>
              </div>
              <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-5 w-5 p-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteEquipment(equipment.id);
                  }}
                >
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </div>
          </div>
        );
      };

      const renderEmptySlot = (side) => (
        <div
          className="relative border border-slate-200 bg-slate-50 hover:bg-blue-50 transition-colors cursor-pointer w-1/2"
          style={{ height: '40px' }}
          onClick={() => {
            setNewEquipment(prev => ({ ...prev, u_position: u, width: `half-${side}` }));
            setShowEquipmentDialog(true);
          }}
        >
          <div className="flex items-center justify-center h-full text-xs text-slate-400">
            Click to add
          </div>
        </div>
      );

      if (fullEq) {
        // Full-width equipment
        rackUnits.push(
          <div key={u} className="relative flex">
            <div className="absolute left-0 top-1/2 -translate-y-1/2 w-8 border-r border-slate-300 bg-slate-100 text-center text-xs font-mono text-slate-600 z-10" style={{ height: `${fullEq.u_height * 40}px`, paddingTop: `${(fullEq.u_height * 40) / 2 - 8}px` }}>
              {u}
            </div>
            <div className="pl-8 flex-1">
              {renderEquipmentBlock(fullEq, 'full')}
            </div>
          </div>
        );
        // Mark all U positions this equipment occupies as rendered
        for (let i = 0; i < fullEq.u_height; i++) {
          renderedUs.add(u - i);
        }
      } else if (leftEq || rightEq) {
        // Half-width equipment
        const maxHeight = Math.max(leftEq?.u_height || 0, rightEq?.u_height || 0);
        rackUnits.push(
          <div key={u} className="relative flex">
            <div className="absolute left-0 top-1/2 -translate-y-1/2 w-8 border-r border-slate-300 bg-slate-100 text-center text-xs font-mono text-slate-600 z-10" style={{ height: `${maxHeight * 40}px`, paddingTop: `${(maxHeight * 40) / 2 - 8}px` }}>
              {u}
            </div>
            <div className="pl-8 flex-1 flex gap-0">
              {leftEq ? renderEquipmentBlock(leftEq, 'half-left') : (!occupied.full.has(u) && !occupied.left.has(u) && renderEmptySlot('left'))}
              {rightEq ? renderEquipmentBlock(rightEq, 'half-right') : (!occupied.full.has(u) && !occupied.right.has(u) && renderEmptySlot('right'))}
            </div>
          </div>
        );
        // Mark all U positions this equipment occupies as rendered
        for (let i = 0; i < maxHeight; i++) {
          renderedUs.add(u - i);
        }
      } else if (!occupied.full.has(u) && !occupied.left.has(u) && !occupied.right.has(u)) {
        // Completely empty U slot
        rackUnits.push(
          <div key={u} className="relative flex">
            <div className="absolute left-0 top-1/2 -translate-y-1/2 w-8 border-r border-slate-200 bg-slate-100 text-center text-xs font-mono text-slate-500 z-10" style={{ height: '40px' }}>
              {u}
            </div>
            <div className="pl-8 flex-1">
              <div
                className="relative border border-slate-200 bg-slate-50 hover:bg-blue-50 transition-colors cursor-pointer"
                style={{ height: '40px' }}
                onClick={() => {
                  setNewEquipment(prev => ({ ...prev, u_position: u, width: 'full' }));
                  setShowEquipmentDialog(true);
                }}
              >
                <div className="flex items-center justify-center h-full text-xs text-slate-400">
                  Click to add equipment
                </div>
              </div>
            </div>
          </div>
        );
      } else if (!occupied.full.has(u)) {
        // Partially occupied - show available half
        rackUnits.push(
          <div key={u} className="relative flex">
            <div className="absolute left-0 top-1/2 -translate-y-1/2 w-8 border-r border-slate-200 bg-slate-100 text-center text-xs font-mono text-slate-500 z-10" style={{ height: '40px' }}>
              {u}
            </div>
            <div className="pl-8 flex-1 flex gap-0">
              {!occupied.left.has(u) && renderEmptySlot('left')}
              {!occupied.right.has(u) && renderEmptySlot('right')}
            </div>
          </div>
        );
      }
    }

    return (
      <div className="bg-gradient-to-b from-slate-200 to-slate-300 p-4 rounded-lg">
        <div className="bg-slate-400 p-2 rounded-t mb-2 text-center text-sm font-semibold text-white">
          {selectedRack.name}
        </div>
        <div className="space-y-0">
          {rackUnits}
        </div>
        <div className="bg-slate-400 p-2 rounded-b mt-2 text-center text-xs text-white">
          {selectedRack.rack_height}U Rack
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (racks.length === 0) {
      return (
        <div className="p-6">
          <Card>
            <CardContent className="py-12 text-center">
              <Server className="w-16 h-16 mx-auto mb-4 text-slate-300" />
              <h3 className="text-xl font-semibold text-slate-700 mb-2">No rack layouts yet</h3>
              <p className="text-slate-500 mb-6">Create your first server rack to visualize equipment placement</p>
              <Button onClick={() => setShowNewRackDialog(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Create First Rack
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    }

    return (
      <div className="p-6 space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-800">Rack Layouts</h2>
          <p className="text-sm text-slate-600">Visual server rack management</p>
        </div>
        <div className="flex gap-2">
          <Select value={selectedRack?.id} onValueChange={(id) => setSelectedRack(racks.find(r => r.id === id))}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Select rack" />
            </SelectTrigger>
            <SelectContent>
              {racks.map(rack => (
                <SelectItem key={rack.id} value={rack.id}>
                  {rack.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={() => setShowNewRackDialog(true)}>
            <Plus className="w-4 h-4 mr-2" />
            New Rack
          </Button>
          {selectedRack && (
            <>
              <Button variant="outline" onClick={openEditRackDialog}>
                <Edit className="w-4 h-4" />
              </Button>
              <Button variant="outline" className="text-red-600" onClick={() => handleDeleteRack(selectedRack.id)}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </>
          )}
        </div>
      </div>

      {selectedRack && (
        <div className="grid lg:grid-cols-4 gap-4">
          {/* Rack Info */}
          <Card className="lg:col-span-1">
            <CardHeader>
              <CardTitle className="text-base">Rack Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-xs text-slate-600">Location</Label>
                <p className="text-sm font-medium">{selectedRack.location || 'Not specified'}</p>
              </div>
              <div>
                <Label className="text-xs text-slate-600">Height</Label>
                <p className="text-sm font-medium">{selectedRack.rack_height}U</p>
              </div>
              <div>
                <Label className="text-xs text-slate-600">Equipment</Label>
                <p className="text-sm font-medium">{selectedRack.equipment?.length || 0} items</p>
              </div>
              <div>
                <Label className="text-xs text-slate-600">Power Usage</Label>
                <p className="text-sm font-medium">
                  {calculateTotalPower()}W
                  {selectedRack.total_power_capacity && (
                    <span className="text-xs text-slate-500">
                      {' '}/ {selectedRack.total_power_capacity}W
                    </span>
                  )}
                </p>
              </div>
              {selectedRack.notes && (
                <div>
                  <Label className="text-xs text-slate-600">Notes</Label>
                  <p className="text-xs text-slate-700">{selectedRack.notes}</p>
                </div>
              )}
              <Button 
                className="w-full" 
                onClick={() => {
                  resetEquipmentForm();
                  setShowEquipmentDialog(true);
                }}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Equipment
              </Button>
            </CardContent>
          </Card>

          {/* Rack Visualization */}
          <div className="lg:col-span-3">
            <Card>
              <CardContent className="p-6">
                <div className="max-w-2xl mx-auto">
                  {renderRackView()}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
    );
  };

  return (
    <>
      {renderContent()}

      {/* Dialogs moved outside main content to fix React Portal issue */}
      {/* New Rack Dialog */}
      <Dialog open={showNewRackDialog} onOpenChange={setShowNewRackDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create New Rack</DialogTitle>
            <DialogDescription>Add a new server rack to this client</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateRack}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="rack-name">Rack Name *</Label>
                <Input
                  id="rack-name"
                  value={newRack.name}
                  onChange={(e) => setNewRack({ ...newRack, name: e.target.value })}
                  placeholder="e.g., Server Room A - Rack 1"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="location">Location</Label>
                <Input
                  id="location"
                  value={newRack.location}
                  onChange={(e) => setNewRack({ ...newRack, location: e.target.value })}
                  placeholder="e.g., Row 3, Position 5"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="rack-height">Height (U)</Label>
                  <Input
                    id="rack-height"
                    type="number"
                    value={newRack.rack_height}
                    onChange={(e) => setNewRack({ ...newRack, rack_height: parseInt(e.target.value) })}
                    min="1"
                    max="100"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="power-capacity">Power Capacity (W)</Label>
                  <Input
                    id="power-capacity"
                    type="number"
                    value={newRack.total_power_capacity || ''}
                    onChange={(e) => setNewRack({ ...newRack, total_power_capacity: e.target.value ? parseInt(e.target.value) : null })}
                    placeholder="Optional"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="rack-notes">Notes</Label>
                <Textarea
                  id="rack-notes"
                  value={newRack.notes}
                  onChange={(e) => setNewRack({ ...newRack, notes: e.target.value })}
                  placeholder="Additional information about this rack"
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowNewRackDialog(false)}>Cancel</Button>
              <Button type="submit">Create Rack</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Rack Dialog */}
      <Dialog open={showEditRackDialog} onOpenChange={setShowEditRackDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Rack</DialogTitle>
            <DialogDescription>Update rack information</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateRack}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit-rack-name">Rack Name *</Label>
                <Input
                  id="edit-rack-name"
                  value={newRack.name}
                  onChange={(e) => setNewRack({ ...newRack, name: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-location">Location</Label>
                <Input
                  id="edit-location"
                  value={newRack.location}
                  onChange={(e) => setNewRack({ ...newRack, location: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-rack-height">Height (U)</Label>
                  <Input
                    id="edit-rack-height"
                    type="number"
                    value={newRack.rack_height}
                    onChange={(e) => setNewRack({ ...newRack, rack_height: parseInt(e.target.value) })}
                    min="1"
                    max="100"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-power-capacity">Power Capacity (W)</Label>
                  <Input
                    id="edit-power-capacity"
                    type="number"
                    value={newRack.total_power_capacity || ''}
                    onChange={(e) => setNewRack({ ...newRack, total_power_capacity: e.target.value ? parseInt(e.target.value) : null })}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-rack-notes">Notes</Label>
                <Textarea
                  id="edit-rack-notes"
                  value={newRack.notes}
                  onChange={(e) => setNewRack({ ...newRack, notes: e.target.value })}
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowEditRackDialog(false)}>Cancel</Button>
              <Button type="submit">Update Rack</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Equipment Dialog */}
      <Dialog open={showEquipmentDialog} onOpenChange={setShowEquipmentDialog}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingEquipment ? 'Edit Equipment' : 'Add Equipment'}</DialogTitle>
            <DialogDescription>Configure rack-mounted equipment</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddEquipment}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="eq-name">Equipment Name *</Label>
                <Input
                  id="eq-name"
                  value={newEquipment.name}
                  onChange={(e) => setNewEquipment({ ...newEquipment, name: e.target.value })}
                  placeholder="e.g., Dell PowerEdge R740"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="eq-type">Equipment Type *</Label>
                <Select
                  value={newEquipment.equipment_type}
                  onValueChange={(value) => setNewEquipment({ ...newEquipment, equipment_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {equipmentTypes.map(type => {
                      const Icon = type.icon;
                      return (
                        <SelectItem key={type.value} value={type.value}>
                          <div className="flex items-center gap-2">
                            <Icon className="w-4 h-4" style={{ color: type.color }} />
                            {type.label}
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="manufacturer">Manufacturer</Label>
                  <Input
                    id="manufacturer"
                    value={newEquipment.manufacturer}
                    onChange={(e) => setNewEquipment({ ...newEquipment, manufacturer: e.target.value })}
                    placeholder="e.g., Dell"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="model">Model</Label>
                  <Input
                    id="model"
                    value={newEquipment.model}
                    onChange={(e) => setNewEquipment({ ...newEquipment, model: e.target.value })}
                    placeholder="e.g., R740"
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="u-position">U Position *</Label>
                  <Input
                    id="u-position"
                    type="number"
                    value={newEquipment.u_position}
                    onChange={(e) => setNewEquipment({ ...newEquipment, u_position: parseInt(e.target.value) })}
                    min="1"
                    max={selectedRack?.rack_height || 42}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="u-height">Height (U) *</Label>
                  <Input
                    id="u-height"
                    type="number"
                    value={newEquipment.u_height}
                    onChange={(e) => setNewEquipment({ ...newEquipment, u_height: parseInt(e.target.value) })}
                    min="1"
                    max="10"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="width">Width *</Label>
                  <Select
                    value={newEquipment.width}
                    onValueChange={(value) => setNewEquipment({ ...newEquipment, width: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="full">Full Width</SelectItem>
                      <SelectItem value="half-left">Half (Left)</SelectItem>
                      <SelectItem value="half-right">Half (Right)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="power">Power (W)</Label>
                  <Input
                    id="power"
                    type="number"
                    value={newEquipment.power_watts || ''}
                    onChange={(e) => setNewEquipment({ ...newEquipment, power_watts: e.target.value ? parseInt(e.target.value) : null })}
                    placeholder="Optional"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="serial">Serial Number</Label>
                  <Input
                    id="serial"
                    value={newEquipment.serial_number}
                    onChange={(e) => setNewEquipment({ ...newEquipment, serial_number: e.target.value })}
                    placeholder="Optional"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="eq-notes">Notes</Label>
                <Textarea
                  id="eq-notes"
                  value={newEquipment.notes}
                  onChange={(e) => setNewEquipment({ ...newEquipment, notes: e.target.value })}
                  placeholder="Additional information"
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => {
                setShowEquipmentDialog(false);
                resetEquipmentForm();
              }}>
                Cancel
              </Button>
              <Button type="submit">
                {editingEquipment ? 'Update' : 'Add'} Equipment
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default RackLayouts;
